import React, {
  createContext,
  useState,
  useRef,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
} from "react";
import Peer from "simple-peer";
import { ICall } from "./types/ws.types";
import { HubConnection } from "@microsoft/signalr";
import { useAppSelector } from "./services/hooks";
import { isAuthSelector } from "./services/selectors/auth.selectors";
import { allConnectedUsersSelector, callEndedSelector, chatConnectionSelector } from "./services/selectors/chat.selector";
import { END_CALL_CODE } from "./utils/constants";
import { userSelector } from "./services/selectors/user.selectors";
import { useLazyInitializeConnectionQuery } from "./services/api/chat.api";
import { useFinalizeCallUploadMutation, useGetIceServersQuery } from "./services/api/user.api";
import {
  StreamingUploader,
  ConnectionQuality as UploadConnectionQuality,
} from "./services/streamingUploader";
import { getSupportedRecordingMimeType, newCallId } from "./utils/recording";
import {
  setActiveRecording,
  clearActiveRecording,
  touchRecordingHeartbeat,
  HEARTBEAT_INTERVAL_MS,
} from "./utils/logoutLock";
interface NetworkInfo {
  effectiveType?: string;
  downlink?: number;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}
type ConnectionQuality = "good" | "slow" | "very-slow";
export type CallRecordingState = "idle" | "recording" | "paused" | "processing";
interface TrackPipeline {
  recorder: MediaRecorder;
  uploader: StreamingUploader;
  startOffsetMs: number;
}
interface CallRecordingHandle {
  sharedCallId: string;
  callStartedAtUtc: string;
  callStartedAtMs: number;
  mimeType: string;
  apiBaseUrl: string;
  patientGuid: string;
  patientUserId: number;
  appointmentId?: number;
  therapist: TrackPipeline;
  patient: TrackPipeline | null;
}
export interface StartCallRecordingOpts {
  patientGuid: string;
  patientName: string;
  patientUserId: number;
  apiBaseUrl: string;
  appointmentId?: number;
}
function getConnectionQuality(): ConnectionQuality {
  const net = (navigator as any).connection as NetworkInfo | undefined;
  if (!net) return "good";
  const type = net.effectiveType;
  if (type === "slow-2g" || type === "2g") return "very-slow";
  if (type === "3g" || (net.downlink !== undefined && net.downlink < 1.5))
    return "slow";
  return "good";
}
function getMediaConstraints(quality: ConnectionQuality): MediaStreamConstraints {
  const audio: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
  };
  if (quality === "very-slow") {
    return {
      video: {
        width: { ideal: 240, max: 320 },
        height: { ideal: 180, max: 240 },
        frameRate: { ideal: 8, max: 10 },
      },
      audio,
    };
  }
  if (quality === "slow") {
    return {
      video: {
        width: { ideal: 320, max: 480 },
        height: { ideal: 240, max: 360 },
        frameRate: { ideal: 10, max: 15 },
      },
      audio,
    };
  }
  return {
    video: {
      width: { ideal: 640, max: 640 },
      height: { ideal: 480, max: 480 },
      frameRate: { ideal: 15, max: 20 },
    },
    audio,
  };
}
function stopRecorder(recorder: MediaRecorder): Promise<void> {
  return new Promise((resolve) => {
    if (recorder.state === "inactive") {
      resolve();
      return;
    }
    recorder.onstop = () => resolve();
    recorder.stop();
  });
}
function worstUploadQuality(
  qualities: (UploadConnectionQuality | undefined)[]
): UploadConnectionQuality {
  const order: UploadConnectionQuality[] = ["offline", "poor", "slow", "good"];
  let worst: UploadConnectionQuality = "good";
  for (const q of qualities) {
    if (!q) continue;
    if (order.indexOf(q) < order.indexOf(worst)) worst = q;
  }
  return worst;
}
export interface ChatSocketCtxState {
  call: ICall;
  setCall: (call: ICall) => void;
  callAccepted: boolean;
  setCallAccepted: Dispatch<SetStateAction<boolean>>;
  endCall: () => void;
  openModalCallEnded: boolean;
  setOpenModalCallEnded: Dispatch<SetStateAction<boolean>>;
  stream: MediaStream | undefined;
  setStream: Dispatch<SetStateAction<MediaStream | undefined>>;
  anotherUserStream: MediaStream | undefined;
  setAnotherUserStream: Dispatch<SetStateAction<MediaStream | undefined>>;
  myVideo: React.MutableRefObject<HTMLVideoElement | null>;
  userVideo: React.MutableRefObject<HTMLVideoElement | null>;
  callUser: (
    socket: HubConnection,
    to: { name: string; id: number; guid?: string },
    from: { name: string; avatar: string | null }
  ) => void;
  leaveCall: () => void;
  startCall: boolean;
  setStartCall: Dispatch<SetStateAction<boolean>>;
  showMediaDevicesErrorPopup: boolean;
  showMediaDevicesNotFoundPopup: boolean;
  setShowMediaDevicesErrorPopup: Dispatch<SetStateAction<boolean>>;
  setShowMediaDevicesNotFoundPopup: Dispatch<SetStateAction<boolean>>;
  startCallRecording: (opts: StartCallRecordingOpts) => Promise<boolean>;
  pauseCallRecording: () => void;
  finalizeCallRecording: () => Promise<{ recordNumber: number } | null>;
  cancelCallRecording: () => Promise<void>;
  callRecordingState: CallRecordingState;
  callRecordingDurationSec: number;
  callRecordingUploadProgress: { uploaded: number; total: number };
  callRecordingConnectionQuality: UploadConnectionQuality;
  hasRemoteStream: boolean;
  onRemoteCallEnded: (callback: () => void) => () => void;
  onRemoteEndAcknowledged: (callback: () => void) => () => void;
  acknowledgeRemoteEnd: () => void;
  replaceVideoTrack: (oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack) => void;
  audioOnly: boolean;
}
const SocketContext = createContext<ChatSocketCtxState>(
  {} as ChatSocketCtxState
);
export const videoCallInitialState: ICall = {
  isReceivingCall: false,
  from: {
    name: "",
    avatar: "",
  },
  signal: { type: "offer" },
  to: { name: "", id: 0 },
};
const ContextProvider = ({ children }: { children: ReactNode }) => {
  const isAuth = useAppSelector(isAuthSelector);
  const connection = useAppSelector(chatConnectionSelector);
  const therapist = useAppSelector(userSelector);
  const connectedUsers = useAppSelector(allConnectedUsersSelector);
  const [finalizeCallUpload] = useFinalizeCallUploadMutation();
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [callAccepted, setCallAccepted] = useState(false);
  const [stream, setStream] = useState<MediaStream>();
  const [anotherUserStream, setAnotherUserStream] = useState<MediaStream>();
  const [call, setCall] = useState<ICall>(videoCallInitialState);
  const [startCall, setStartCall] = useState(false);
  const [openModalCallEnded, setOpenModalCallEnded] = useState(false);
  const [showMediaDevicesErrorPopup, setShowMediaDevicesErrorPopup] =
    useState(false);
  const [showMediaDevicesNotFoundPopup, setShowMediaDevicesNotFoundPopup] =
    useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  const [callRecordingState, setCallRecordingState] =
    useState<CallRecordingState>("idle");
  const [callRecordingDurationSec, setCallRecordingDurationSec] = useState(0);
  const [callRecordingUploadProgress, setCallRecordingUploadProgress] = useState({
    uploaded: 0,
    total: 0,
  });
  const [callRecordingConnectionQuality, setCallRecordingConnectionQuality] =
    useState<UploadConnectionQuality>("good");
  const [hasRemoteStream, setHasRemoteStream] = useState(false);
  const myVideo = useRef<HTMLVideoElement>(null);
  const userVideo = useRef<HTMLVideoElement>(null);
  const connectionRef = useRef<Peer.Instance | null>();
  const remoteEndCallbacksRef = useRef<Set<() => void>>(new Set());
  const remoteEndAckCallbacksRef = useRef<Set<() => void>>(new Set());
  const connectionListenerRef = useRef<(() => void) | null>(null);
  const recordingHandleRef = useRef<CallRecordingHandle | null>(null);
  const callRecordingStateRef = useRef<CallRecordingState>("idle");
  const durationTimerRef = useRef<number | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const callRecordingDurationSecRef = useRef(0);
  const { data: dynamicIceServers } = useGetIceServersQuery();
  const iceServers =
    dynamicIceServers && Array.isArray(dynamicIceServers.iceServers)
      ? dynamicIceServers.iceServers
      : [];
  const syncRecordingProgress = useCallback(() => {
    const handle = recordingHandleRef.current;
    if (!handle) return;
    const t = handle.therapist.uploader.getUploadStatus();
    const p = handle.patient?.uploader.getUploadStatus();
    setCallRecordingUploadProgress({
      uploaded: (t?.uploaded ?? 0) + (p?.uploaded ?? 0),
      total: (t?.total ?? 0) + (p?.total ?? 0),
    });
    setCallRecordingConnectionQuality(
      worstUploadQuality([
        handle.therapist.uploader.getConnectionStats().quality,
        handle.patient?.uploader.getConnectionStats().quality,
      ])
    );
  }, []);
  const setRecordingState = useCallback((state: CallRecordingState) => {
    callRecordingStateRef.current = state;
    setCallRecordingState(state);
  }, []);
  const [connect] = useLazyInitializeConnectionQuery();
  useEffect(() => {
    if (isAuth && therapist.id) {
      connect(therapist.id);
    }
  }, [connectedUsers, isAuth, therapist.id, connect]);
  const adaptToConnectionQuality = useCallback((activeStream: MediaStream) => {
    const quality = getConnectionQuality();
    if (quality === "very-slow") {
      activeStream.getVideoTracks().forEach((track) => {
        track.enabled = false;
      });
      setAudioOnly(true);
      return;
    }
    const wasAudioOnly = !activeStream.getVideoTracks().some((tr) => tr.enabled);
    if (wasAudioOnly) {
      activeStream.getVideoTracks().forEach((track) => {
        track.enabled = true;
      });
      setAudioOnly(false);
    }
    const videoTrack = activeStream.getVideoTracks()[0];
    if (!videoTrack) return;
    const constraints = getMediaConstraints(quality).video;
    if (typeof constraints === "object") {
      videoTrack.applyConstraints(constraints).catch((err) => {
        // console.error("Failed to apply adaptive constraints:", err);
      });
    }
  }, []);
  const startConnectionWatcher = useCallback(
    (activeStream: MediaStream) => {
      stopConnectionWatcher();
      const net = (navigator as any).connection as NetworkInfo | undefined;
      if (!net?.addEventListener) return;
      const handler = () => adaptToConnectionQuality(activeStream);
      net.addEventListener("change", handler);
      connectionListenerRef.current = () =>
        net.removeEventListener!("change", handler);
    },
    [adaptToConnectionQuality]
  );
  const stopConnectionWatcher = useCallback(() => {
    connectionListenerRef.current?.();
    connectionListenerRef.current = null;
  }, []);
  const startPatientTrack = useCallback(
    (remoteStream: MediaStream, apiBaseUrl: string) => {
      const handle = recordingHandleRef.current;
      if (!handle || handle.patient) return;
      const audioTracks = remoteStream.getAudioTracks();
      if (audioTracks.length === 0) return;
      const mimeType = handle.mimeType;
      const patientUploader = new StreamingUploader({
        apiBaseUrl,
        userId: therapist.id?.toString() ?? "0",
        patientName: call.to.name,
        patientGuid: handle.patientGuid,
        appointmentId: handle.appointmentId,
        track: "patient",
        sharedCallId: handle.sharedCallId,
        onProgress: () => syncRecordingProgress(),
        onConnectionQuality: () => syncRecordingProgress(),
      });
      patientUploader.setMimeType(mimeType);
      const patientStream = new MediaStream(audioTracks);
      const patientRecorder = new MediaRecorder(
        patientStream,
        mimeType ? { mimeType } : undefined
      );
      patientRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) patientUploader.addChunk(e.data);
      };
      const startOffsetMs = Date.now() - handle.callStartedAtMs;
      patientRecorder.start(2000);
      handle.patient = {
        recorder: patientRecorder,
        uploader: patientUploader,
        startOffsetMs,
      };
      syncRecordingProgress();
    },
    [call.to.name, syncRecordingProgress, therapist.id]
  );
  const startCallRecording = useCallback(
    async (opts: StartCallRecordingOpts): Promise<boolean> => {
      // Resume from paused state — same session, same uploaders, just restart recorders
      if (callRecordingStateRef.current === "paused") {
        const handle = recordingHandleRef.current;
        if (!handle) return false;
        try {
          if (handle.therapist.recorder.state === "paused") handle.therapist.recorder.resume();
          if (handle.patient?.recorder && handle.patient.recorder.state === "paused") {
            handle.patient.recorder.resume();
          }
        } catch (err) {
          // console.error("[CallRecording] Failed to resume recorders:", err);
          return false;
        }
        setRecordingState("recording");
        syncRecordingProgress();
        return true;
      }

      if (!stream || recordingHandleRef.current) return false;
      const mimeType = getSupportedRecordingMimeType() || "audio/webm";
      const sharedCallId = newCallId();
      const callStartedAtUtc = new Date().toISOString();
      const callStartedAtMs = Date.now();
      const therapistUploader = new StreamingUploader({
        apiBaseUrl: opts.apiBaseUrl,
        userId: therapist.id?.toString() ?? "0",
        patientName: opts.patientName,
        patientGuid: opts.patientGuid,
        appointmentId: opts.appointmentId,
        track: "therapist",
        sharedCallId,
        onProgress: () => syncRecordingProgress(),
        onConnectionQuality: () => syncRecordingProgress(),
      });
      therapistUploader.setMimeType(mimeType);
      const therapistAudioStream = new MediaStream(stream.getAudioTracks());
      const therapistRecorder = new MediaRecorder(
        therapistAudioStream,
        mimeType ? { mimeType } : undefined
      );
      therapistRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) therapistUploader.addChunk(e.data);
      };
      therapistRecorder.start(2000);
      recordingHandleRef.current = {
        sharedCallId,
        callStartedAtUtc,
        callStartedAtMs,
        mimeType,
        apiBaseUrl: opts.apiBaseUrl,
        patientGuid: opts.patientGuid,
        patientUserId: opts.patientUserId,
        appointmentId: opts.appointmentId,
        therapist: {
          recorder: therapistRecorder,
          uploader: therapistUploader,
          startOffsetMs: 0,
        },
        patient: null,
      };
      setActiveRecording({
        sessionId: sharedCallId,
        patientGuid: opts.patientGuid,
        userId: opts.patientUserId.toString(),
        patientName: opts.patientName,
      });
      if (anotherUserStream) {
        startPatientTrack(anotherUserStream, opts.apiBaseUrl);
      }
      callRecordingDurationSecRef.current = 0;
      setCallRecordingDurationSec(0);
      setRecordingState("recording");
      syncRecordingProgress();
      return true;
    },
    [
      stream,
      anotherUserStream,
      therapist.id,
      setRecordingState,
      startPatientTrack,
      syncRecordingProgress,
    ]
  );
  const leaveCall = useCallback(() => {
    setCall(videoCallInitialState);
    setCallAccepted(false);
    setStartCall(false);
    setOpenModalCallEnded(true);
    setAudioOnly(false);
    setHasRemoteStream(false);
    stopConnectionWatcher();
    if (connectionRef.current) {
      connectionRef.current.destroy();
      connectionRef.current = null;
    }
    if (callRecordingStateRef.current === "idle") {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(undefined);
      }
      setAnotherUserStream(undefined);
    }
  }, [stream, stopConnectionWatcher]);

  const finalizeCallRecording = useCallback(async (): Promise<{
    recordNumber: number;
  } | null> => {
    setRecordingState("processing");
    const handle = recordingHandleRef.current;
    if (!handle) {
      setRecordingState("idle");
      return null;
    }
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    await Promise.all([
      stopRecorder(handle.therapist.recorder),
      handle.patient ? stopRecorder(handle.patient.recorder) : Promise.resolve(),
    ]);
    await Promise.all([
      handle.therapist.uploader.flushAndStop(),
      handle.patient?.uploader.flushAndStop() ?? Promise.resolve(),
    ]);
    const therapistStatus = handle.therapist.uploader.getUploadStatus();
    const patientStatus = handle.patient?.uploader.getUploadStatus();
    try {
      const result = await finalizeCallUpload({
        therapistSessionId: handle.therapist.uploader.getSessionId(),
        patientSessionId: handle.patient?.uploader.getSessionId() ?? null,
        sharedCallId: handle.sharedCallId,
        usersGuid: handle.patientGuid,
        therapistTotalChunks: therapistStatus.total,
        patientTotalChunks: patientStatus?.total ?? 0,
        therapistStartOffsetMs: handle.therapist.startOffsetMs,
        patientStartOffsetMs: handle.patient?.startOffsetMs ?? null,
        mimeType: handle.mimeType,
        durationSeconds: callRecordingDurationSecRef.current,
        recordingStartedAtUtc: handle.callStartedAtUtc,
        appointmentId: handle.appointmentId,
      }).unwrap();
      if (result.success) {
        await handle.therapist.uploader.purgeStoredChunks();
        handle.patient?.uploader.purgeStoredChunks();
        handle.therapist.uploader.cleanup();
        handle.patient?.uploader.cleanup();
      }
      recordingHandleRef.current = null;
      clearActiveRecording();
      setRecordingState("idle");
      // leaveCall();
      return result.success && result.recordNumber != null
        ? { recordNumber: result.recordNumber }
        : null;
    } catch (err) {
      // console.error("[CallRecording] finalize failed:", err);
      handle.therapist.uploader.cleanup();
      handle.patient?.uploader.cleanup();
      recordingHandleRef.current = null;
      setRecordingState("idle");
      leaveCall();
      return null;
    }
  }, [finalizeCallUpload, setRecordingState, leaveCall]);
  const cancelCallRecording = useCallback(async () => {
    const handle = recordingHandleRef.current;
    if (!handle) return;
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    try {
      if (handle.therapist.recorder.state !== "inactive") handle.therapist.recorder.stop();
      const patientRecorder = handle.patient?.recorder;
      if (patientRecorder && patientRecorder.state !== "inactive") patientRecorder.stop();
    } catch {
      /* ignore */
    }
    await Promise.all([
      handle.therapist.uploader.cancel(),
      handle.patient?.uploader.cancel() ?? Promise.resolve(),
    ]);
    handle.therapist.uploader.cleanup();
    handle.patient?.uploader.cleanup();
    recordingHandleRef.current = null;
    clearActiveRecording();
    setRecordingState("idle");
  }, [setRecordingState]);

  const pauseCallRecording = useCallback(() => {
    const handle = recordingHandleRef.current;
    if (!handle || callRecordingStateRef.current !== "recording") return;
    try {
      if (handle.therapist.recorder.state === "recording") handle.therapist.recorder.pause();
      if (handle.patient?.recorder && handle.patient.recorder.state === "recording") {
        handle.patient.recorder.pause();
      }
    } catch (err) {
      // console.error("[CallRecording] Failed to pause recorders:", err);
    }
    setRecordingState("paused");
  }, [setRecordingState]);

  const callUser = useCallback(async (
    socket: HubConnection,
    to: { name: string; id: number; guid?: string },
    from: { name: string; avatar: string | null }
  ) => {
    const quality = getConnectionQuality();
    const constraints = getMediaConstraints(quality);
    const currentStream = await navigator?.mediaDevices
      ?.getUserMedia(constraints)
      .catch((err) => {
        if (
          err?.name?.includes("NotReadableError") ||
          err?.name?.includes("AbortError")
        ) {
          setShowMediaDevicesNotFoundPopup(true);
        } else {
          setShowMediaDevicesErrorPopup(true);
        }
      });
    try {
      if (currentStream) {
        if (quality === "very-slow") {
          currentStream.getVideoTracks().forEach((tr) => (tr.enabled = false));
          setAudioOnly(true);
        } else {
          setAudioOnly(false);
        }
        if (myVideo.current) {
          myVideo.current.srcObject = currentStream;
        }
        setStream(currentStream);
        setStartCall(true);
        startConnectionWatcher(currentStream);
        // console.log("[ICE] TURN servers from API:", JSON.stringify(iceServers, null, 2));
        const peer = new Peer({
          initiator: true,
          trickle: false,
          stream: currentStream,
          config: {
            iceServers: [
              { urls: "stun:stun.l.google.com:19302" },
              { urls: "stun:stun1.l.google.com:19302" },
              { urls: "stun:stun.services.mozilla.com" },
              ...iceServers,
            ],
          },
        });
        // Verify TURN — addEventListener preserves simple-peer's internal handler
        // "host" = local IP, "srflx" = STUN public IP, "relay" = TURN relay ✅
        // const pc = (peer as any)._pc as RTCPeerConnection;
        // pc.addEventListener("icecandidate", (e: RTCPeerConnectionIceEvent) => {
        //   // if (e.candidate) {
        //   //   console.log("[ICE]", e.candidate.type, e.candidate.candidate);
        //   // } else {
        //   //   console.log("[ICE] gathering complete");
        //   // }
        // });
        // pc.addEventListener("icegatheringstatechange", () => {
        //   console.log("[ICE] gatheringState →", pc.iceGatheringState);
        // });
        setCall({ ...videoCallInitialState, to, from });
        peer.on("signal", (data: Peer.SignalData) => {
          socket.invoke("SendOffer", to.id, from, JSON.stringify(data));
        });
        peer.on("stream", (remoteStream) => {
          setAnotherUserStream(remoteStream);
          setHasRemoteStream(true);
          if (userVideo.current) userVideo.current.srcObject = remoteStream;
          if (recordingHandleRef.current) {
            startPatientTrack(remoteStream, recordingHandleRef.current.apiBaseUrl);
          }
        });
        peer.on("close", () => {
          socket.off("ReceiveAnswer");
          if (connectionRef.current) {
            connectionRef.current = null;
          }
        });
        socket.on("ReceiveAnswer", (signal) => {
          setCallAccepted(true);
          const parsed = JSON.parse(signal);
          if (peer && parsed.type === "answer") {
            peer.signal(signal);
          }
        });
        connectionRef.current = peer;
        peer.on("error", (err) => console.error("Peer error:", err));
      } else {
        setShowMediaDevicesNotFoundPopup(true);
      }
    } catch (err: any) {
      if (
        err?.name?.includes("NotReadableError") ||
        err?.name?.includes("AbortError")
      ) {
        setShowMediaDevicesNotFoundPopup(true);
      } else {
        setShowMediaDevicesErrorPopup(true);
      }
    }
  }, [startConnectionWatcher, startPatientTrack, iceServers]);

  const endCall = useCallback(() => {
    if (connection) {
      connection.invoke("SendMessage", {
        senderId: Number(therapist.id),
        type: END_CALL_CODE,
        content: `${END_CALL_CODE}:by:${call.from.name}`,
        receiverId: call.to.id,
      });
    }
    if (callRecordingStateRef.current === "idle") {
      leaveCall();
    }
  }, [connection, therapist.id, call, leaveCall]);

  const onRemoteCallEnded = useCallback((callback: () => void): () => void => {
    remoteEndCallbacksRef.current.add(callback);
    if (connection) {
      connection.on("RemoteCallEnded", callback);
    }
    return () => {
      remoteEndCallbacksRef.current.delete(callback);
      if (connection) {
        connection.off("RemoteCallEnded", callback);
      }
    };
  }, [connection]);

  const onRemoteEndAcknowledged = useCallback((callback: () => void): () => void => {
    remoteEndAckCallbacksRef.current.add(callback);
    return () => {
      remoteEndAckCallbacksRef.current.delete(callback);
    };
  }, []);

  const acknowledgeRemoteEnd = useCallback(() => {
    remoteEndAckCallbacksRef.current.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        // console.error(e);
      }
    });
  }, []);
  const callEndedBy = useAppSelector(callEndedSelector);
  useEffect(() => {
    if (callEndedBy) {
      remoteEndCallbacksRef.current.forEach((cb) => {
        try {
          cb();
        } catch (e) {
          // console.error(e);
        }
      });
    }
  }, [callEndedBy]);
  useEffect(() => {
    return () => stopConnectionWatcher();
  }, [stopConnectionWatcher]);
  // Late patient stream while recording
  useEffect(() => {
    const handle = recordingHandleRef.current;
    if (anotherUserStream && handle && !handle.patient) {
      startPatientTrack(anotherUserStream, handle.apiBaseUrl);
    }
  }, [anotherUserStream, startPatientTrack]);
  // Duration + heartbeat while recording (stops when paused or idle)
  useEffect(() => {
    if (callRecordingState !== "recording") {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      return;
    }
    durationTimerRef.current = window.setInterval(() => {
      setCallRecordingDurationSec((s) => {
        const next = s + 1;
        callRecordingDurationSecRef.current = next;
        recordingHandleRef.current?.therapist.uploader.setDurationSeconds(next);
        recordingHandleRef.current?.patient?.uploader.setDurationSeconds(next);
        return next;
      });
      syncRecordingProgress();
    }, 1000);
    heartbeatTimerRef.current = window.setInterval(() => {
      touchRecordingHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
    return () => {
      if (durationTimerRef.current) clearInterval(durationTimerRef.current);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    };
  }, [callRecordingState, syncRecordingProgress]);
  const replaceVideoTrack = useCallback((
    oldTrack: MediaStreamTrack,
    newTrack: MediaStreamTrack
  ) => {
    if (!connectionRef.current || !stream) return;
    try {
      connectionRef.current.replaceTrack(oldTrack, newTrack, stream);
    } catch (err) {
      // console.error("replaceVideoTrack failed:", err);
    }
  }, [stream]);
  return (
    <SocketContext.Provider
      value={{
        call,
        setCall,
        callAccepted,
        myVideo,
        userVideo,
        stream,
        setStream,
        callUser,
        leaveCall,
        startCall,
        setStartCall,
        setCallAccepted,
        openModalCallEnded,
        setOpenModalCallEnded,
        anotherUserStream,
        setAnotherUserStream,
        showMediaDevicesErrorPopup,
        setShowMediaDevicesErrorPopup,
        showMediaDevicesNotFoundPopup,
        setShowMediaDevicesNotFoundPopup,
        endCall,
        startCallRecording,
        pauseCallRecording,
        finalizeCallRecording,
        cancelCallRecording,
        callRecordingState,
        callRecordingDurationSec,
        callRecordingUploadProgress,
        callRecordingConnectionQuality,
        hasRemoteStream,
        onRemoteCallEnded,
        onRemoteEndAcknowledged,
        acknowledgeRemoteEnd,
        replaceVideoTrack,
        audioOnly,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
export { ContextProvider, SocketContext };
